/**
 * CallLogsSummary — client-side summary for the Call Logs report.
 *
 * The backend handler returns only `records`, so all aggregates are derived
 * here: total calls, total + average talk time, and a per-call-type breakdown.
 *
 * call_length is a "HH:MM:SS" (or "MM:SS") string per core_event_calls.
 */

import { useMemo } from 'react';

// "HH:MM:SS" | "MM:SS" | "SS" → total seconds (0 on bad/empty input).
function parseDurationToSeconds(value) {
    if (!value) return 0;
    const parts = String(value).split(':').map((p) => parseInt(p, 10));
    if (parts.some((n) => Number.isNaN(n))) return 0;
    if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
    if (parts.length === 2) return parts[0] * 60 + parts[1];
    if (parts.length === 1) return parts[0];
    return 0;
}

// seconds → "H:MM:SS" (hours only when > 0), else "M:SS"
function formatSeconds(total) {
    const t = Math.max(0, Math.round(total));
    const h = Math.floor(t / 3600);
    const m = Math.floor((t % 3600) / 60);
    const s = t % 60;
    const pad = (n) => String(n).padStart(2, '0');
    return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
}

const CallLogsSummary = ({ records }) => {
    const stats = useMemo(() => {
        const rows = records || [];
        const total = rows.length;

        let totalSeconds = 0;
        const byType = new Map();
        rows.forEach((r) => {
            totalSeconds += parseDurationToSeconds(r.call_length);
            const name = r.call_type_name || 'Unknown';
            byType.set(name, (byType.get(name) || 0) + 1);
        });

        const avgSeconds = total > 0 ? totalSeconds / total : 0;
        const breakdown = [...byType.entries()]
            .map(([name, count]) => ({ name, count }))
            .sort((a, b) => b.count - a.count);

        return { total, totalSeconds, avgSeconds, breakdown };
    }, [records]);

    return (
        <div className="cl-summary">
            {/* Stat cards */}
            <div className="cl-summary__cards">
                <div className="ar-summary-card">
                    <div className="ar-summary-card__value">{stats.total.toLocaleString()}</div>
                    <div className="ar-summary-card__label">Total Calls</div>
                </div>
                <div className="ar-summary-card">
                    <div className="ar-summary-card__value">{formatSeconds(stats.totalSeconds)}</div>
                    <div className="ar-summary-card__label">Total Talk Time</div>
                </div>
                <div className="ar-summary-card">
                    <div className="ar-summary-card__value">{formatSeconds(stats.avgSeconds)}</div>
                    <div className="ar-summary-card__label">Avg Call Length</div>
                </div>
                <div className="ar-summary-card">
                    <div className="ar-summary-card__value">{stats.breakdown.length.toLocaleString()}</div>
                    <div className="ar-summary-card__label">Call Types</div>
                </div>
            </div>

            {/* By call type breakdown */}
            <div className="pp-section" style={{ marginTop: 'var(--space-4)' }}>
                <div className="pp-section__header">
                    <h3 className="pp-section__title">Calls by Type</h3>
                </div>
                <div className="pp-section__body">
                    {stats.breakdown.length === 0 ? (
                        <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-muted)' }}>
                            No calls for the selected period.
                        </span>
                    ) : (
                        <ul className="cl-breakdown">
                            {stats.breakdown.map((b) => {
                                const pct = stats.total > 0 ? Math.round((b.count / stats.total) * 100) : 0;
                                return (
                                    <li key={b.name} className="cl-breakdown__item">
                                        <span className="cl-breakdown__name">{b.name}</span>
                                        <span className="cl-breakdown__bar-wrap" aria-hidden="true">
                                            <span className="cl-breakdown__bar" style={{ width: `${pct}%` }} />
                                        </span>
                                        <span className="cl-breakdown__count">{b.count.toLocaleString()} ({pct}%)</span>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CallLogsSummary;
